// Global
import {
  GetStaticComponentProps,
  ImageField,
  LinkField,
  RichTextField,
  Text,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useTheme } from 'lib/context/ThemeContext';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';

// Components
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { mapSearchResults } from 'lib/graphql/mappers/map-search-results';
import { IntegratedGraphQlResult } from 'lib/graphql/types/integrated-graphql-result';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import { ItemSearchResults } from 'lib/graphql/types/item-search-results';
import { getEnum } from 'lib/utils/get-enum';
import { BodyCopy } from 'src/helpers/BodyCopy';
import Component from 'src/helpers/Component/Component';
import Headline from 'src/helpers/Headline/Headline';
import LinkWrapper from 'src/helpers/LinkWrapper/LinkWrapper';
import ImageWrapper from 'src/helpers/Media/ImageWrapper';
import { RichTextWrapper } from 'src/helpers/RichTextWrapper';
import { GenericChartTheme, GetTextAlignment } from './GenericChart.theme';

export type GenericChartProps =
  Feature.EnterpriseWeb.AndersenWindows.Components.General.GenericChart.GenericChart & {
    fields: {
      children: [
        Feature.EnterpriseWeb.AndersenWindows.Components.General.GenericChart.GenericChartColumn
      ];
    };
  };

export type GenericChartColumnProps =
  Feature.EnterpriseWeb.AndersenWindows.Components.General.GenericChart.GenericChartColumn;

const RenderTitleContent = (column: GenericChartColumnProps) => {
  if (!column?.fields) {
    return <></>;
  }

  return (
    <>
      {!column.fields.titleLink?.value?.href ? (
        column.fields.title?.value && (
          <h3>
            <Text field={column.fields.title} encode={false}></Text>
          </h3>
        )
      ) : (
        <LinkWrapper
          field={column.fields.titleLink}
          ariaLabel={{ value: column.fields.title?.value || 'Title Link' }}
        >
          {column.fields.title?.value ? (
            <h3>
              <span className="border-black hover:border-b-2">
                <Text field={column.fields.title} encode={false}></Text>
              </span>
            </h3>
          ) : (
            <h3>{column.fields.titleLink.value?.text}</h3>
          )}
        </LinkWrapper>
      )}
      {!column.fields.subtitleLink?.value?.href ? (
        column.fields.subtitle?.value && (
          <h4>
            <Text field={column.fields.subtitle}></Text>
          </h4>
        )
      ) : (
        <LinkWrapper
          field={column.fields.subtitleLink}
          ariaLabel={{ value: column.fields.subtitle?.value || 'Subtitle Link' }}
        >
          {column.fields.subtitle?.value ? (
            <h4>
              <span className="border-black hover:border-b-2">
                <Text field={column.fields.subtitle} encode={false}></Text>
              </span>
            </h4>
          ) : (
            <h4>{column.fields.subtitleLink.value?.text}</h4>
          )}
        </LinkWrapper>
      )}
    </>
  );
};

export type TextAlignment = 'left' | 'center' | 'right' | '';
export type BackgroundColor = 'black' | 'gray' | 'white';

const GenericChart = (props: GenericChartProps) => {
  const alignment = getEnum<TextAlignment>(props.fields?.textAlignment) || 'left';
  const titleRowBackgroundColor =
    getEnum<BackgroundColor>(props.fields?.titleRowBackgroundColor) || 'black';
  const titleColumnBackgroundColor =
    getEnum<BackgroundColor>(props.fields?.titleColumnBackgroundColor) || 'gray';
  const { themeData } = useTheme(
    GenericChartTheme(titleRowBackgroundColor, titleColumnBackgroundColor, alignment)
  );
  const MAX_CHART_ROWS = 25;

  // Get the number of colums
  const columnCount = props.fields?.children?.length;
  let rowCount = 0;
  // Loop through each column and it's rows to determine
  // the total number of rows to display
  for (let col = 0; col < columnCount; col++) {
    let tempRowCount = 0;
    for (let i = 1; i <= MAX_CHART_ROWS; i++) {
      if (props.fields?.children[col]?.fields[`row${i}Content`]?.value) {
        tempRowCount++;
        if (tempRowCount > rowCount) {
          rowCount = tempRowCount;
        }
      }
    }
  }

  const countToMaxChartRowsArray = [...Array(rowCount + 1).keys()].splice(1);

  // Used to determine if we should write a <th></th> at the beginning of each row
  const hasRowTitles = !!countToMaxChartRowsArray.find(
    (count: number) => !!props.fields[`row${count}Title`]?.value
  );

  const hasColumnTitles = !!countToMaxChartRowsArray.find(
    (count: number) =>
      !!props.fields?.children[count]?.fields[`title`]?.value ||
      !!props.fields?.children[count]?.fields[`subtitle`]?.value
  );

  const hasSchemaText = !!props.fields.schemaName?.value && !!props.fields.schemaDescription?.value;
  const hasImageRow = !!props.fields.children.find(
    (_: Feature.EnterpriseWeb.AndersenWindows.Components.General.GenericChart.GenericChartColumn) =>
      !!_.fields?.image?.value?.src
  );
  const hasImageColumn = !!countToMaxChartRowsArray.find(
    (count: number) => !!props.fields[`row${count}Image`]?.value?.src
  );

  return (
    <Component
      sectionWrapperClasses={themeData.classes.section}
      variant="lg"
      grid=" w-full "
      dataComponent="general/genericchart"
      {...props}
    >
      <Headline
        {...props}
        classes={' text-[26px] font-demi leading-[1.25] mb-[12px] md:text-[34px] '}
      ></Headline>
      <BodyCopy
        {...props}
        classes={' hidden md:block text-[18px] leading-1.5] mb-[10px] '}
      ></BodyCopy>
      <div className="comparison-chart-table">
        {hasSchemaText && (
          <>
            <meta itemProp="name" content={props.fields.schemaName?.value} />
            <meta itemProp="description" content={props.fields.schemaDescription?.value} />
          </>
        )}

        <table className={themeData.classes.table}>
          {hasColumnTitles && (
            <thead>
              <tr className={themeData.classes.theadRow}>
                {hasImageColumn && <th className={themeData.classes.imageColumnHeader}>&nbsp;</th>}
                {hasRowTitles && <th>&nbsp;</th>}
                {props.fields.children.map((column: GenericChartColumnProps, index: number) => (
                  <th
                    className={themeData.classes.rowHeader}
                    key={'GenericChartColumnProps_th_' + index}
                  >
                    {RenderTitleContent(column)}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {hasImageRow && (
              <tr className={themeData.classes.imageRow}>
                {hasImageColumn && <td className={themeData.classes.imageColumn}>&nbsp;</td>}
                {hasRowTitles && <th>&nbsp;</th>}
                {props.fields.children.map((column: GenericChartColumnProps, index: number) => (
                  <td key={'GenericChartColumnProps_td_' + index} className="p-[5px]">
                    {!column?.fields?.imageLink?.value?.href ? (
                      <ImageWrapper
                        image={column?.fields?.image}
                        additionalDesktopClasses="lazy"
                        imageLayout="intrinsic"
                      ></ImageWrapper>
                    ) : (
                      <LinkWrapper
                        field={column?.fields?.imageLink}
                        ariaLabel={{ value: 'GenericChart image' }}
                      >
                        <ImageWrapper
                          image={column?.fields?.image}
                          additionalDesktopClasses="lazy"
                          imageLayout="intrinsic"
                        ></ImageWrapper>
                      </LinkWrapper>
                    )}
                  </td>
                ))}
              </tr>
            )}
            {countToMaxChartRowsArray.map((rowNumber: number, index: number) => {
              const rowTitle = props.fields[`row${rowNumber}Title`] as TextField;
              const rowTitleLink = props.fields[`row${rowNumber}Link`] as LinkField;
              const rowSubtitle = props.fields[`row${rowNumber}Subtitle`] as TextField;
              const rowSubtitleLink = props.fields[`row${rowNumber}SubtitleLink`] as LinkField;
              const rowImage = props.fields[`row${rowNumber}Image`] as ImageField;
              const rowImageLink = props.fields[`row${rowNumber}ImageLink`] as LinkField;
              return (
                (rowCount > 0 || !!rowTitleLink?.value?.href) && (
                  <tr className={themeData.classes.tableRow} key={'content-row-' + index}>
                    {hasImageColumn && (
                      <td className={themeData.classes.imageColumn}>
                        {rowImage?.value?.src &&
                          (!rowImageLink?.value?.href ? (
                            <>
                              <ImageWrapper image={rowImage}></ImageWrapper>
                            </>
                          ) : (
                            <>
                              <LinkWrapper field={rowImageLink} ariaLabel={{ value: 'Row Image' }}>
                                <ImageWrapper image={rowImage}></ImageWrapper>
                              </LinkWrapper>
                            </>
                          ))}
                      </td>
                    )}
                    {hasRowTitles && (
                      <th className={themeData.classes.columnHeader}>
                        {!rowTitleLink?.value?.href ? (
                          rowTitle?.value && (
                            <h4>
                              <Text field={rowTitle} encode={false}></Text>
                            </h4>
                          )
                        ) : (
                          <LinkWrapper
                            field={rowTitleLink}
                            ariaLabel={{
                              value: (rowTitleLink?.value?.text as string) || 'Row Title Link',
                            }}
                          >
                            {rowTitle?.value ? (
                              <h4>
                                <Text field={rowTitle} encode={false}></Text>
                              </h4>
                            ) : (
                              <h4>{rowTitleLink?.value?.text}</h4>
                            )}
                          </LinkWrapper>
                        )}
                        {!rowSubtitleLink?.value?.href ? (
                          rowSubtitle?.value && (
                            <h5>
                              <Text field={rowSubtitle} encode={false}></Text>
                            </h5>
                          )
                        ) : (
                          <LinkWrapper
                            field={rowSubtitleLink}
                            ariaLabel={{
                              value:
                                (rowSubtitleLink?.value?.text as string) || 'Row Subtitle Link',
                            }}
                          >
                            {rowSubtitle?.value ? (
                              <h5>
                                <Text field={rowSubtitle} encode={false}></Text>
                              </h5>
                            ) : (
                              <h5>{rowSubtitleLink?.value?.text}</h5>
                            )}
                          </LinkWrapper>
                        )}
                      </th>
                    )}
                    {props.fields.children.map(
                      (
                        column: Feature.EnterpriseWeb.AndersenWindows.Components.General.GenericChart.GenericChartColumn,
                        index: number
                      ) => {
                        const textAlignment =
                          column.fields?.textAlignment != null
                            ? getEnum<TextAlignment>(column.fields.textAlignment) || ''
                            : '';
                        const rowContent = (column?.fields?.[
                          `row${rowNumber}Content` as keyof typeof column.fields
                        ] || { value: '' }) as RichTextField;
                        return (
                          <td
                            className={`${themeData.classes.tableData} ${GetTextAlignment(
                              textAlignment
                            )}`}
                            key={'content_td_' + index}
                          >
                            {hasColumnTitles && (
                              <div className={themeData.classes.mobileTitleColumn}>
                                {RenderTitleContent(column)}
                              </div>
                            )}
                            <RichTextWrapper
                              field={rowContent}
                              classes={themeData.classes.contentClass}
                            ></RichTextWrapper>
                          </td>
                        );
                      }
                    )}
                  </tr>
                )
              );
            })}
          </tbody>
        </table>
      </div>
      {props.fields.footer?.value && (
        <div className={themeData.classes.footer}>
          <RichTextWrapper field={props.fields.footer}></RichTextWrapper>
        </div>
      )}
    </Component>
  );
};

type IntegratedGraphQl = IntegratedGraphQlResult<{
  item: {
    fields: ItemFieldResult[];
    children: ItemSearchResults<{
      fields: ItemFieldResult[];
    }>;
  };
}>;

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  if (rendering.fields === undefined || !('data' in rendering.fields)) {
    return {};
  }

  const fields = rendering.fields as unknown as IntegratedGraphQl;

  const result = {
    fields: {
      ...mapItemFieldResultsToObject(fields.data.item.fields),
      children: mapSearchResults(fields.data.item.children, (child) => {
        return {
          fields: {
            ...mapItemFieldResultsToObject(child.fields),
          },
        };
      }),
    },
  };
  return result;
};

export default withDatasourceCheck()<GenericChartProps>(GenericChart);
